import { useCallback, useEffect, useMemo } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout';
import {
  dispatchHidePopupMenu,
  dispatchShowPopupMenu,
} from '~/services/plugins/popup-menu/components/host/index.ts';

export const AUDIO_PLAYER_POPUP_MENU_Z_INDEX: number = 11000;

export const AUDIO_PLAYER_POPUP_MENU_OWNER: string = 'audio-player';

const ADD_TO_PLAYLIST_ALIAS: string = 'add_to_playlist';

const AUDIO_PLAYER_FALLBACK_ENTRY_ACTIONS = [
  { button: { alias: 'add_to_queue' }, dismiss_on_action: true },
  { button: { alias: 'go_to_queue' }, dismiss_on_action: true },
];

export type UseAudioPlayerPopupMenuReturnType = {
  showMoreMenu: (triggerElement: HTMLElement | null) => void;
  showAddToPlaylist: (triggerElement: HTMLElement | null) => void;
};

/**
 * Whether the entry brings its own popup menu actions.
 * @returns true when `extensions.entry_action` is a non-empty array
 */
function hasEntryActions(entry: FeedEntry | null): boolean {
  const entryActions: unknown = entry?.extensions?.entry_action;

  return Array.isArray(entryActions) && entryActions.length > 0;
}

/**
 * The audio player's menu entry: the entry itself when it has actions,
 * otherwise the entry with the queue fallback actions.
 * @param entry - the current track, null while none is loaded
 * @returns the entry shown by the audio player Popup Menu
 */
export function getAudioPlayerPopupMenuEntry(
  entry: FeedEntry | null
): FeedEntry {
  if (entry && hasEntryActions(entry)) {
    return entry;
  }

  return {
    ...entry,
    extensions: {
      ...(entry?.extensions || {}),
      entry_action: AUDIO_PLAYER_FALLBACK_ENTRY_ACTIONS,
    },
  } as FeedEntry;
}

/**
 * Popup Triggers of the expanded and mini audio players.
 * @param entry - the current track, null while none is loaded
 * @returns openers for the "more" menu and the add-to-playlist quick action
 */
export function useAudioPlayerPopupMenu(
  entry: FeedEntry | null
): UseAudioPlayerPopupMenuReturnType {
  const feed: Feed = useMemo(
    () => ({ entry: [getAudioPlayerPopupMenuEntry(entry)] } as Feed),
    [entry]
  );

  const uiComponent: UIComponent = useMemo(() => {
    const entryId: string =
      typeof entry?.id === 'string' ? entry.id : 'audio-entry';

    return { id: `audio-player-popup-menu-${entryId}` } as UIComponent;
  }, [entry?.id]);

  useEffect(() => {
    if (!entry?.id) {
      return undefined;
    }

    return (): void => {
      dispatchHidePopupMenu({ owner: AUDIO_PLAYER_POPUP_MENU_OWNER });
    };
  }, [entry?.id]);

  const showPopupMenu = useCallback(
    ({
      triggerElement,
      quickActionAlias,
    }: {
      triggerElement: HTMLElement | null;
      quickActionAlias?: string;
    }): void => {
      if (!triggerElement) {
        return;
      }

      dispatchShowPopupMenu({
        triggerElement,
        source: 'click',
        owner: AUDIO_PLAYER_POPUP_MENU_OWNER,
        uiComponent,
        content: { kind: 'feed', feed },
        zIndex: AUDIO_PLAYER_POPUP_MENU_Z_INDEX,
        quickActionAlias,
      });
    },
    [feed, uiComponent]
  );

  return useMemo(
    () => ({
      showMoreMenu: (triggerElement: HTMLElement | null): void =>
        showPopupMenu({ triggerElement }),
      showAddToPlaylist: (triggerElement: HTMLElement | null): void =>
        showPopupMenu({
          triggerElement,
          quickActionAlias: ADD_TO_PLAYLIST_ALIAS,
        }),
    }),
    [showPopupMenu]
  );
}
